import React, { useState } from "react";
import { Link } from "react-router-dom";
import useEscapeLayer from "../useEscape";
import { addEditor, addRequest, industryOptions } from "./scoreAdd.mjs";

/*
 * SCORE BREAKDOWN: the rows that ADD UP to the score (frame 1c; edge states 1i).
 *
 * The old card worded the score as a verdict ("33/100 · below the threshold of
 * 40") with its reasons in a hover whose chips added to 22 under a 33. Here each
 * fact the scorer read, the row it matched and the points ("computer software →
 * B2B SaaS · +14") are one row, the rows and the floor sum to the total, and the
 * total is the badge. A penalty reads "−4" in plain text: it is arithmetic, not
 * a warning, so it is never red.
 *
 * Unknown facts read "unknown · Add" because the score is live: Add opens an
 * editor in the row, the write goes where the scorer reads (record/scoreAdd.mjs),
 * and the shell's reload brings the new score back into this same card. The
 * 100-point bar is in three parts from one sentence ("Lost 6 to missing data, 14
 * to fit."): the score black, missing data an EMPTY segment with a dashed grey
 * edge (never the hatch, which means on hold), the rest of the hundred in the
 * ramp's lightest grey. The 40 line (the after-hours threshold) is mentioned only
 * here.
 *
 * Edge states: blocked says the phrase that blocked it and where; a score read
 * off a colleague says whose facts these are ("Scored on Shahar Bahat (point of
 * contact)"), because the title row is theirs; the arrival score shows only when
 * it differs from today's.
 *
 * Opened by a click on the badge (the header), or on Fit's "Breakdown" where the
 * score has left the header ("Would score 33 today"). Esc closes it, after
 * anything opened on top of it.
 *
 * Props:
 *   record   the shell's bundle; reads record.score (scoreView), company.id,
 *            company.lead_score.contact_id (who was scored), contact.id
 *   ops      ops.toggleBreakdown (closes), ops.patchCompany, ops.patchContact,
 *            ops.setProps (each reloads, so the score refetches), ops.call (the
 *            industry values)
 *   title    string | null, defaults to "Score 80 · hot"
 */
export default function ScoreBreakdown({ record, ops, title = null }) {
  const s = record.score;
  const [editing, setEditing] = useState(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [industries, setIndustries] = useState(null);
  useEscapeLayer(!!(s && s.show), () => ops.toggleBreakdown());
  if (!s || !s.show) return null;

  const company = record.company || null;
  const ls = (company && company.lead_score) || {};
  const ctx = {
    companyId: company ? company.id : null,
    contactId: record.contact ? record.contact.id : null,
    scoredContactId: ls.contact_id || null,
  };

  const open = (row) => {
    setError(null);
    setValue("");
    setEditing(row.group);
    // The industry picker offers only values the column already holds, read
    // once per breakdown and only when someone actually adds an industry.
    if (row.group === "industry" && industries === null) {
      setIndustries("loading");
      ops
        .call("/contacts?stats=1&limit=1")
        .then((r) => setIndustries(industryOptions(r && r.facets)))
        .catch((e) => {
          setIndustries(null);
          setError({ group: "industry", message: `Couldn't load the industries: ${e.message}` });
        });
    }
  };
  const cancel = () => {
    setEditing(null);
    setError(null);
  };
  const save = async (row) => {
    const req = addRequest(row, value, ctx);
    if (req.error) return setError({ group: row.group, message: req.error });
    setBusy(true);
    setError(null);
    try {
      if (req.kind === "company") await ops.patchCompany(req.body);
      else if (req.kind === "contact") await ops.patchContact(req.id, req.body);
      else await ops.setProps("company", req.id, req.props);
      setEditing(null);
    } catch (e) {
      setError({ group: row.group, message: e.message });
    } finally {
      setBusy(false);
    }
  };

  const editorFor = (row) => {
    const ed = addEditor(row);
    if (!ed) return null;
    const onSubmit = (e) => {
      e.preventDefault();
      save(row);
    };
    return (
      <form className="rec-bk-edit" onSubmit={onSubmit}>
        {ed.input === "select" ? (
          <select className="rec-select" aria-label={ed.label} value={value} autoFocus onChange={(e) => setValue(e.target.value)} disabled={busy}>
            <option value="">{industries === "loading" ? "Loading…" : ed.placeholder}</option>
            {(Array.isArray(industries) ? industries : []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        ) : (
          <input
            className="rec-input"
            type="text"
            inputMode={ed.input === "number" ? "numeric" : undefined}
            aria-label={ed.label}
            placeholder={ed.placeholder}
            value={value}
            autoFocus
            disabled={busy}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
        <button type="submit" className="rec-btn rec-btn--dark rec-btn--sm" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        <button type="button" className="rec-link rec-link--quiet" onClick={cancel} disabled={busy}>Cancel</button>
      </form>
    );
  };

  const bar = s.bar || { score: 0, missing: 0, fit: 0 };

  return (
    <div className="rec-card rec-breakdown" role="region" aria-label="Score breakdown">
      <div className="rec-card__head">
        <span className="rec-breakdown__title">{title || `Score ${s.label}`}</span>
        <button type="button" className="rec-x" title="Close (Esc)" aria-label="Close the breakdown" onClick={() => ops.toggleBreakdown()}>×</button>
      </div>
      {s.scoredOn && <div className="rec-sub">{s.scoredOn}</div>}

      {s.blocked ? (
        <div className="rec-body">{s.blockedLine}</div>
      ) : (
        <>
          <div>
            <div className="rec-bar" role="img" aria-label={`${s.score} of 100. ${s.sentence}`}>
              {bar.score > 0 && <span className="rec-bar__score" style={{ width: `${bar.score}%` }} />}
              {bar.missing > 0 && <span className="rec-bar__missing" style={{ width: `${bar.missing}%` }} />}
              {bar.fit > 0 && <span className="rec-bar__fit" style={{ width: `${bar.fit}%` }} />}
            </div>
            <div className="rec-breakdown__sentence">{s.sentence}</div>
          </div>

          <div className="rec-breakdown__grid">
            <span className="rec-bk-head">Group</span>
            <span className="rec-bk-head">What we read · row it matched</span>
            <span className="rec-bk-head rec-bk-points">Points</span>
            {s.rows.map((r) => (
              <React.Fragment key={r.group}>
                <span className="rec-bk-group">{r.name}</span>
                <span className={`rec-bk-read${r.unknown ? " is-unknown" : ""}`}>
                  {editing === r.group ? (
                    editorFor(r)
                  ) : (
                    <>
                      {r.read}
                      {addEditor(r) && (
                        <>
                          {" · "}
                          <button type="button" className="rec-link rec-bk-add" onClick={() => open(r)}>{r.add.label}</button>
                        </>
                      )}
                    </>
                  )}
                </span>
                <span className={`rec-bk-points${r.points === 0 ? " is-zero" : ""}`}>{r.display}</span>
                {error && error.group === r.group && <span className="rec-bk-err rec-err" role="alert">{error.message}</span>}
              </React.Fragment>
            ))}
            {s.floor && (
              <>
                <span className="rec-bk-group">{s.floor.name}</span>
                <span className="rec-bk-read">{s.floor.read}</span>
                <span className="rec-bk-points">{s.floor.display}</span>
              </>
            )}
            <span className="rec-bk-total">Total</span>
            <span className="rec-bk-total" />
            <span className="rec-bk-total rec-bk-points">{s.total}</span>
          </div>
        </>
      )}

      <div className="rec-breakdown__foot">
        {s.arrival && <span>{s.arrival}</span>}
        {/* A blocked score stays 0 whatever is filled in, so it makes no such promise. */}
        {!s.blocked && <span>Live: fill in a fact and the score changes.</span>}
        <span>
          {s.bandsLine}
          {s.thresholdLine ? ` ${s.thresholdLine}` : ""}
        </span>
        <Link className="rec-breakdown__weights" to="/inbound/scoring">Edit the weights in Scoring</Link>
      </div>
    </div>
  );
}
